import { useMemo } from "react"
import {
  DAY_KEYS,
  useScheduleAvailabilityConfig,
} from "../utils/scheduleAvailabilityConfig"

function getFinancialStatus(event) {
  const ext = event?.extendedProps || {}

  const explicit = String(
    ext.financialStatus || event?.financialStatus || "",
  ).toUpperCase()

  if (["PAID", "PARTIAL", "DEBT", "NONE"].includes(explicit)) {
    return explicit
  }

  const produced = Number(ext.producedAmount ?? event?.producedAmount ?? 0)
  const paid = Number(ext.paidAmount ?? event?.paidAmount ?? 0)
  const pending = Number(ext.pendingAmount ?? event?.pendingAmount ?? 0)

  if (produced <= 0) return "NONE"
  if (pending <= 0 && paid > 0) return "PAID"
  if (paid > 0 && pending > 0) return "PARTIAL"
  if (pending > 0) return "DEBT"
  return "NONE"
}

function getLocalDateKey(value) {
  if (!value) return ""

  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ""

  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, "0")
  const dd = String(date.getDate()).padStart(2, "0")

  return `${yyyy}-${mm}-${dd}`
}

function getTargetDate(date, events) {
  if (date) {
    const parsed = new Date(`${String(date).slice(0, 10)}T12:00:00`)
    if (!Number.isNaN(parsed.getTime())) return parsed
  }

  const firstEvent = (Array.isArray(events) ? events : []).find((event) => event?.start)
  if (firstEvent?.start) {
    const parsed = new Date(firstEvent.start)
    if (!Number.isNaN(parsed.getTime())) return parsed
  }

  return new Date()
}

function isSameLocalDay(value, dateKey) {
  return getLocalDateKey(value) === dateKey
}

function buildLocalDateTime(dateKey, time) {
  const [hoursRaw, minutesRaw] = String(time || "00:00").split(":")
  const hours = Number(hoursRaw || 0)
  const minutes = Number(minutesRaw || 0)

  const date = new Date(`${dateKey}T00:00:00`)
  date.setHours(hours, minutes, 0, 0)
  return date
}

function normalizeBlockedDates(text) {
  return String(text || "")
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => item.slice(0, 10))
}

function getDayConfig(scheduleConfig, date) {
  const dayKey = DAY_KEYS[date.getDay()]
  return scheduleConfig?.hours?.[dayKey] || null
}

function getWorkingIntervals(scheduleConfig, targetDate) {
  const dateKey = getLocalDateKey(targetDate)
  const blockedDates = normalizeBlockedDates(scheduleConfig?.breaks?.blockedDatesText)
  const dayConfig = getDayConfig(scheduleConfig, targetDate)

  if (blockedDates.includes(dateKey)) {
    return {
      dateKey,
      blocked: true,
      nonWorking: false,
      intervals: [],
      totalMinutes: 0,
      reason: "Fecha bloqueada por configuración de agenda.",
    }
  }

  if (!dayConfig || !dayConfig.enabled) {
    return {
      dateKey,
      blocked: false,
      nonWorking: true,
      intervals: [],
      totalMinutes: 0,
      reason: "Día no laborable según la configuración de agenda.",
    }
  }

  const workStart = buildLocalDateTime(dateKey, dayConfig.start)
  const workEnd = buildLocalDateTime(dateKey, dayConfig.end)

  if (workEnd <= workStart) {
    return {
      dateKey,
      blocked: false,
      nonWorking: true,
      intervals: [],
      totalMinutes: 0,
      reason: "La jornada configurada no tiene una ventana válida.",
    }
  }

  const intervals = []
  const breaks = scheduleConfig?.breaks || {}

  if (breaks.lunchEnabled) {
    const lunchStart = buildLocalDateTime(dateKey, breaks.lunchStart || "14:00")
    const lunchEnd = buildLocalDateTime(dateKey, breaks.lunchEnd || "15:00")

    if (lunchStart > workStart && lunchEnd < workEnd && lunchEnd > lunchStart) {
      intervals.push({ start: workStart, end: lunchStart })
      intervals.push({ start: lunchEnd, end: workEnd })
    } else {
      intervals.push({ start: workStart, end: workEnd })
    }
  } else {
    intervals.push({ start: workStart, end: workEnd })
  }

  const totalMinutes = intervals.reduce(
    (acc, interval) =>
      acc + Math.max(0, Math.round((interval.end.getTime() - interval.start.getTime()) / 60000)),
    0,
  )

  return {
    dateKey,
    blocked: false,
    nonWorking: false,
    intervals,
    totalMinutes,
    reason: "",
  }
}

function eventIsHeld(event) {
  return (
    String(event?.id || "").startsWith("held-") ||
    event?.extendedProps?.isHeld ||
    event?.extendedProps?.status === "HELD"
  )
}

function eventIsAppointment(event) {
  return !eventIsHeld(event)
}

function getTimedEvents(events, dateKey) {
  return (Array.isArray(events) ? events : [])
    .filter((event) => event?.start && event?.end)
    .map((event) => ({
      ...event,
      _start: new Date(event.start),
      _end: new Date(event.end),
    }))
    .filter(
      (event) =>
        !Number.isNaN(event._start.getTime()) &&
        !Number.isNaN(event._end.getTime()) &&
        event._end > event._start &&
        (isSameLocalDay(event._start, dateKey) || isSameLocalDay(event._end, dateKey)),
    )
    .sort((a, b) => a._start.getTime() - b._start.getTime())
}

function clampIntervalToWorking(event, workingIntervals) {
  const pieces = []

  workingIntervals.forEach((interval) => {
    const start = new Date(Math.max(event._start.getTime(), interval.start.getTime()))
    const end = new Date(Math.min(event._end.getTime(), interval.end.getTime()))

    if (end > start) {
      pieces.push({ start, end })
    }
  })

  return pieces
}

function mergeBusyIntervals(intervals) {
  const sorted = intervals
    .filter((interval) => interval?.start && interval?.end && interval.end > interval.start)
    .sort((a, b) => a.start.getTime() - b.start.getTime())

  const merged = []

  sorted.forEach((interval) => {
    const last = merged[merged.length - 1]

    if (!last || interval.start > last.end) {
      merged.push({
        start: new Date(interval.start),
        end: new Date(interval.end),
      })
      return
    }

    if (interval.end > last.end) {
      last.end = new Date(interval.end)
    }
  })

  return merged
}

function getMinutesBetween(start, end) {
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000))
}

function getBusyMinutes(timedEvents, workingIntervals) {
  const busyPieces = []

  timedEvents.forEach((event) => {
    const pieces = clampIntervalToWorking(event, workingIntervals)
    busyPieces.push(...pieces)
  })

  const merged = mergeBusyIntervals(busyPieces)

  return merged.reduce(
    (acc, interval) => acc + getMinutesBetween(interval.start, interval.end),
    0,
  )
}

function getGapsInsideWorkingIntervals(timedEvents, workingIntervals) {
  const busyPieces = []

  timedEvents.forEach((event) => {
    const pieces = clampIntervalToWorking(event, workingIntervals)
    busyPieces.push(...pieces)
  })

  const busy = mergeBusyIntervals(busyPieces)
  const gaps = []

  workingIntervals.forEach((workInterval) => {
    let cursor = new Date(workInterval.start)

    busy
      .filter((busyInterval) => busyInterval.end > workInterval.start && busyInterval.start < workInterval.end)
      .forEach((busyInterval) => {
        if (busyInterval.start > cursor) {
          const minutes = getMinutesBetween(cursor, busyInterval.start)

          if (minutes > 0) {
            gaps.push({
              start: new Date(cursor),
              end: new Date(busyInterval.start),
              minutes,
            })
          }
        }

        if (busyInterval.end > cursor) {
          cursor = new Date(busyInterval.end)
        }
      })

    if (cursor < workInterval.end) {
      const minutes = getMinutesBetween(cursor, workInterval.end)

      if (minutes > 0) {
        gaps.push({
          start: new Date(cursor),
          end: new Date(workInterval.end),
          minutes,
        })
      }
    }
  })

  return gaps
}

function eventIsOutsideWorkingTime(event, workingIntervals) {
  if (!workingIntervals.length) return true

  const coveredPieces = clampIntervalToWorking(event, workingIntervals)
  const coveredMinutes = coveredPieces.reduce(
    (acc, piece) => acc + getMinutesBetween(piece.start, piece.end),
    0,
  )

  const eventMinutes = getMinutesBetween(event._start, event._end)

  return eventMinutes > 0 && coveredMinutes < eventMinutes
}

function formatTime(date) {
  return date.toLocaleTimeString("es-MX", {
    hour: "2-digit",
    minute: "2-digit",
  })
}

function buildGapSuggestion(minutes) {
  if (minutes >= 30 && minutes < 45) {
    return "Ideal para limpieza, revisión o consulta de seguimiento."
  }

  if (minutes >= 45 && minutes < 60) {
    return "Ideal para resina, revisión con procedimiento corto o urgencia simple."
  }

  if (minutes >= 60 && minutes < 90) {
    return "Buen espacio para cita de plan, corona o procedimiento intermedio."
  }

  if (minutes >= 90) {
    return "Excelente espacio para tratamiento de mayor valor o bloque clínico importante."
  }

  return "Espacio útil para continuidad operativa."
}

function getLoadLevel(occupationPct) {
  if (occupationPct >= 85) {
    return {
      type: "SATURATION",
      level: "HIGH",
      badge: "Alta saturación",
      title: `Agenda al ${occupationPct}% de carga`,
      description: "Conviene vigilar retrasos, tiempos clínicos y redistribución de carga.",
    }
  }

  if (occupationPct >= 65) {
    return {
      type: "STABLE_LOAD",
      level: "MEDIUM",
      badge: "Carga media-alta",
      title: `Agenda al ${occupationPct}% de carga`,
      description: "Día operativo estable, pero con margen limitado para insertar nuevas citas.",
    }
  }

  if (occupationPct >= 30) {
    return {
      type: "HEALTHY_LOAD",
      level: "LOW",
      badge: "Carga saludable",
      title: `Agenda al ${occupationPct}% de carga`,
      description: "La jornada tiene actividad sin estar saturada.",
    }
  }

  return {
    type: "LOW_LOAD",
    level: "POSITIVE",
    badge: "Carga baja",
    title: `Agenda al ${occupationPct}% de carga`,
    description: "Hay buena disponibilidad para nuevas citas, recall o reacomodos.",
  }
}

export default function useAgendaDerivedMetrics({
  clinicId,
  date,
  events,
  treatmentPlans,
  backendOpportunityItems,
  selectedAppointment,
  viewMode,
}) {
  const schedule = useScheduleAvailabilityConfig(clinicId)

  return useMemo(() => {
    const safeEvents = Array.isArray(events) ? events : []
    const safePlans = Array.isArray(treatmentPlans) ? treatmentPlans : []
    const safeBackendOpportunities = Array.isArray(backendOpportunityItems)
      ? backendOpportunityItems
      : []

    const targetDate = getTargetDate(date, safeEvents)
    const working = getWorkingIntervals(schedule.config, targetDate)

    const timedEvents = getTimedEvents(safeEvents, working.dateKey)
    const appointmentEvents = timedEvents.filter(eventIsAppointment)
    const heldEvents = timedEvents.filter(eventIsHeld)

    const busyMinutes = getBusyMinutes(timedEvents, working.intervals)
    const appointmentBusyMinutes = getBusyMinutes(appointmentEvents, working.intervals)
    const heldBusyMinutes = getBusyMinutes(heldEvents, working.intervals)
    const idleMinutes = Math.max(0, working.totalMinutes - busyMinutes)

    const occupationPct =
      working.totalMinutes > 0
        ? Math.min(100, Math.round((busyMinutes / working.totalMinutes) * 100))
        : 0

    const totalAppointments = appointmentEvents.length
    const heldAppointments = heldEvents.length

    const recallAppointments = appointmentEvents.filter(
      (e) => e?.source === "RECALL" || e?.extendedProps?.source === "RECALL",
    ).length

    const planAppointments = appointmentEvents.filter(
      (e) => e?.extendedProps?.isPlanLinked,
    ).length

    const paidAppointments = appointmentEvents.filter(
      (e) => getFinancialStatus(e) === "PAID",
    ).length

    const partialAppointments = appointmentEvents.filter(
      (e) => getFinancialStatus(e) === "PARTIAL",
    ).length

    const debtAppointments = appointmentEvents.filter(
      (e) => getFinancialStatus(e) === "DEBT",
    ).length

    const producedAmount = appointmentEvents.reduce(
      (acc, event) =>
        acc + Number(event?.extendedProps?.producedAmount ?? event?.producedAmount ?? 0),
      0,
    )

    const collectedAmount = appointmentEvents.reduce(
      (acc, event) =>
        acc + Number(event?.extendedProps?.paidAmount ?? event?.paidAmount ?? 0),
      0,
    )

    const pendingAmount = appointmentEvents.reduce(
      (acc, event) =>
        acc + Number(event?.extendedProps?.pendingAmount ?? event?.pendingAmount ?? 0),
      0,
    )

    const agendaStats = {
      totalAppointments,
      heldAppointments,
      recallAppointments,
      planAppointments,
      paidAppointments,
      partialAppointments,
      debtAppointments,
      producedAmount,
      collectedAmount,
      pendingAmount,
      idleMinutes,
      busyMinutes,
      appointmentBusyMinutes,
      heldBusyMinutes,
      operationalMinutes: working.totalMinutes,
      occupationPct,
    }

    const agendaStatsForView =
      viewMode === "OPERATIVE"
        ? agendaStats
        : viewMode === "CLINICAL"
          ? {
              totalAppointments: agendaStats.totalAppointments,
              heldAppointments: agendaStats.planAppointments,
              recallAppointments: agendaStats.recallAppointments,
              planAppointments: selectedAppointment ? 1 : agendaStats.planAppointments,
              paidAppointments: agendaStats.paidAppointments,
              partialAppointments: agendaStats.partialAppointments,
              debtAppointments: agendaStats.debtAppointments,
              producedAmount: agendaStats.producedAmount,
              collectedAmount: agendaStats.collectedAmount,
              pendingAmount: agendaStats.pendingAmount,
              idleMinutes: agendaStats.idleMinutes,
              busyMinutes: agendaStats.busyMinutes,
              appointmentBusyMinutes: agendaStats.appointmentBusyMinutes,
              heldBusyMinutes: agendaStats.heldBusyMinutes,
              operationalMinutes: agendaStats.operationalMinutes,
              occupationPct: agendaStats.occupationPct,
            }
          : agendaStats

    const actionItems = appointmentEvents.flatMap((event) => {
      const checkedIn = Boolean(event?.extendedProps?.checkedIn)
      const isPlanLinked = Boolean(event?.extendedProps?.isPlanLinked)
      const patientName = event?.extendedProps?.patientName || event?.title || "Paciente"
      const treatmentName =
        event?.extendedProps?.treatmentName ||
        event?.extendedProps?.reason ||
        "Sin tratamiento definido"

      const status = getFinancialStatus(event)
      const items = []

      if (!checkedIn) {
        items.push({
          type: "CHECKIN_PENDING",
          severity: "MEDIUM",
          badge: "Sin llegada confirmada",
          title: patientName,
          description: `${treatmentName}. Aún no se ha marcado llegada.`,
          appointmentId: event.id,
        })
      }

      if (checkedIn && status === "DEBT") {
        items.push({
          type: "PAYMENT_PENDING",
          severity: "HIGH",
          badge: "Adeudo activo",
          title: patientName,
          description: `${treatmentName}. La cita ya avanzó, pero quedó saldo pendiente.`,
          appointmentId: event.id,
        })
      }

      if (checkedIn && status === "PARTIAL") {
        items.push({
          type: "PARTIAL_PAYMENT",
          severity: "MEDIUM",
          badge: "Abono pendiente",
          title: patientName,
          description: `${treatmentName}. Ya hubo abono, pero todavía falta cerrar el cobro.`,
          appointmentId: event.id,
        })
      }

      if (checkedIn && isPlanLinked) {
        items.push({
          type: "PLAN_FOLLOWUP",
          severity: "LOW",
          badge: "Cita ligada a plan",
          title: patientName,
          description: `${treatmentName}. Verifica avance clínico y continuidad del plan.`,
          appointmentId: event.id,
        })
      }

      return items
    })

    const delayRiskItems = appointmentEvents.flatMap((event, index) => {
      const next = appointmentEvents[index + 1]
      if (!next) return []

      const gapMinutes = Math.round(
        (next._start.getTime() - event._end.getTime()) / 60000,
      )

      if (gapMinutes >= 0 && gapMinutes < 5) {
        return [
          {
            type: "CHAIN_DELAY_RISK",
            severity: "HIGH",
            badge: "Riesgo de retraso",
            title: event?.extendedProps?.patientName || event?.title || "Paciente",
            description: `Solo hay ${gapMinutes} min antes de la siguiente cita. El día puede encadenar retrasos.`,
            appointmentId: event.id,
          },
        ]
      }

      return []
    })

    const gaps = getGapsInsideWorkingIntervals(timedEvents, working.intervals)
    const usefulGaps = gaps.filter((gap) => gap.minutes >= 30)
    const largeIdleGaps = gaps.filter((gap) => gap.minutes >= 60)

    const opportunityItems = []

    appointmentEvents.forEach((event) => {
      const patientName = event?.extendedProps?.patientName || event?.title || "Paciente"
      const treatmentName =
        event?.extendedProps?.treatmentName ||
        event?.extendedProps?.reason ||
        "Sin tratamiento definido"

      if (event?.extendedProps?.isPlanLinked) {
        opportunityItems.push({
          type: "PLAN_CONTINUATION",
          title: patientName,
          description: `Tratamiento ${treatmentName} ligado a plan. Conviene asegurar continuidad.`,
          appointmentId: event.id,
        })
      }
    })

    largeIdleGaps.forEach((gap) => {
      const startLabel = formatTime(gap.start)
      const endLabel = formatTime(gap.end)

      opportunityItems.push({
        type: "IDLE_TIME",
        title: `Hueco ${startLabel} - ${endLabel}`,
        description: `${gap.minutes} min libres dentro de la jornada configurada. ${buildGapSuggestion(gap.minutes)}`,
        appointmentId: null,
        gapStart: gap.start,
        gapEnd: gap.end,
      })
    })

    const recallOpportunityItems = []

    safePlans.forEach((plan) => {
      const pendingItems = (plan.items || []).filter(
        (item) =>
          item.status !== "DONE" &&
          item.status !== "CANCELLED" &&
          !item.appointmentId &&
          !item.treatmentLogId,
      )

      if (!pendingItems.length) return

      const firstPending = pendingItems[0]

      recallOpportunityItems.push({
        type: "PLAN_PENDING_PATIENT",
        title: `${plan.patientName || "Paciente con plan pendiente"}`,
        description: `${pendingItems.length} tratamiento(s) pendientes. Siguiente sugerido: ${firstPending.treatmentName || "Tratamiento"}.`,
        patientId: plan.patientId,
        treatmentId: firstPending.treatmentId || "",
        planItemId: firstPending.id || "",
      })
    })

    const outsideScheduleEvents = timedEvents.filter((event) =>
      eventIsOutsideWorkingTime(event, working.intervals),
    )

    const capacityItems = []

    if (working.blocked || working.nonWorking || working.totalMinutes <= 0) {
      capacityItems.push({
        type: working.blocked ? "BLOCKED_DAY" : "NON_WORKING_DAY",
        level: "LOW",
        badge: working.blocked ? "Fecha bloqueada" : "Día no laborable",
        title: working.reason || "No hay jornada configurada para este día",
        description:
          timedEvents.length > 0
            ? `Hay ${timedEvents.length} cita(s) visibles fuera de una jornada operativa configurada.`
            : "No se calcula saturación porque este día no tiene jornada activa.",
      })
    } else if (!timedEvents.length) {
      capacityItems.push({
        type: "EMPTY_DAY",
        level: "LOW",
        badge: "Agenda libre",
        title: `${working.totalMinutes} min disponibles en la jornada`,
        description: "Buen momento para captar citas, activar recall o revisar disponibilidad.",
      })
    } else {
      const loadItem = getLoadLevel(occupationPct)

      capacityItems.push({
        ...loadItem,
        description: `${busyMinutes} min ocupados de ${working.totalMinutes} min disponibles. ${loadItem.description}`,
      })

      if (usefulGaps.length > 0) {
        const firstGap = [...usefulGaps].sort((a, b) => b.minutes - a.minutes)[0]

        capacityItems.push({
          type: "USEFUL_GAPS",
          level: "POSITIVE",
          badge: "Espacio disponible",
          title: `${usefulGaps.length} hueco(s) dentro de la jornada`,
          description: `El mayor espacio libre es de ${firstGap.minutes} min, de ${formatTime(firstGap.start)} a ${formatTime(firstGap.end)}.`,
        })
      }

      if (largeIdleGaps.length > 0 && occupationPct < 65) {
        const biggestGap = [...largeIdleGaps].sort((a, b) => b.minutes - a.minutes)[0]

        capacityItems.push({
          type: "LARGE_IDLE_GAP",
          level: "LOW",
          badge: "Capacidad libre",
          title: `${biggestGap.minutes} min continuos disponibles`,
          description: "Puede aprovecharse para tratamientos de mayor duración, recall o reacomodos.",
        })
      }
    }

    if (outsideScheduleEvents.length > 0) {
      capacityItems.push({
        type: "OUTSIDE_SCHEDULE_EVENTS",
        level: "MEDIUM",
        badge: "Fuera de horario",
        title: `${outsideScheduleEvents.length} cita(s) fuera de jornada`,
        description: "Revisa si son excepciones reales o si conviene ajustar la configuración de horario.",
      })
    }

    const financialOpportunityItems = []

    if (debtAppointments > 0) {
      financialOpportunityItems.push({
        type: "FINANCIAL_DEBT_SUMMARY",
        title: "Citas con adeudo",
        description: `${debtAppointments} cita(s) siguen con saldo pendiente.`,
        scoreFinal: 95,
      })
    }

    if (partialAppointments > 0) {
      financialOpportunityItems.push({
        type: "FINANCIAL_PARTIAL_SUMMARY",
        title: "Citas con abono",
        description: `${partialAppointments} cita(s) tienen abono y aún no están cerradas.`,
        scoreFinal: 85,
      })
    }

    const allOpportunityItems = [
      ...opportunityItems,
      ...recallOpportunityItems,
      ...financialOpportunityItems,
      ...safeBackendOpportunities,
    ].sort((a, b) => Number(b.scoreFinal || b.score || 0) - Number(a.scoreFinal || a.score || 0))

    return {
      idleMinutes,
      agendaStats,
      agendaStatsForView,
      actionItems,
      delayRiskItems,
      capacityItems,
      opportunityItems,
      recallOpportunityItems,
      allOpportunityItems,
    }
  }, [
    date,
    events,
    treatmentPlans,
    backendOpportunityItems,
    selectedAppointment,
    viewMode,
    schedule.config,
  ])
}